// src/admin/screens/admins/modals/ResetTotpModal.tsx
// Calls the admin_reset_totp RPC (shimmed in FUNCTION_EXTRAS until
// sub-project A ships the real migration). The RPC:
//   - verifies caller is super_admin
//   - deletes auth.mfa_factors rows for the target auth user
//   - sets admin_users.totp_enrolled_at = null
//
// Target admin is forced to re-enrol TOTP on next sign-in; any current
// AAL2 session becomes AAL1 on next refresh.
import { useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import { recordAudit } from '@admin/lib/audit';
import Modal from '@admin/components/Modal';
import Button from '@admin/components/Button';
import FormField from '@admin/components/FormField';
import { useToast } from '@admin/components/ToastProvider';
import { translateError } from '@admin/utils/translateError';
import type { AdminRow } from '@admin/hooks/useAdmins';
import styles from './AdminModals.module.css';

interface Props {
  admin: AdminRow;
  onClose: (refresh: boolean) => void;
}

export default function ResetTotpModal({ admin, onClose }: Props) {
  const toast = useToast();
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (reason.trim().length < 10) {
      toast({ kind: 'error', msg: 'Reason must be at least 10 characters.' });
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.rpc('admin_reset_totp', {
        p_target_admin_id: admin.id,
      });
      if (error) throw error;
      await recordAudit({
        action: 'admin.reset_totp',
        target_type: 'admin',
        target_id: admin.id,
        payload: { reason: reason.trim() },
      });
      toast({
        kind: 'success',
        msg: 'TOTP cleared. The admin must re-enrol on next sign-in.',
      });
      await qc.invalidateQueries({ queryKey: ['admin', admin.id] });
      await qc.invalidateQueries({ queryKey: ['admins'] });
      onClose(true);
    } catch (err) {
      toast({ kind: 'error', msg: translateError(err as Error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => onClose(false)}
      title={`Reset TOTP — ${admin.display_name}`}
    >
      <form onSubmit={submit} className={styles.form}>
        <p className={styles.lead}>
          Use when an admin has lost their phone. They'll be forced to re-enrol
          TOTP on their next sign-in, and any current AAL2 session is invalidated
          on JWT refresh.
        </p>
        <FormField
          label="Reason"
          htmlFor="rt-reason"
          hint="Required and audit-logged."
          required
        >
          <textarea
            id="rt-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </FormField>
        <div className={styles.actions}>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onClose(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button type="submit" loading={busy}>
            Reset TOTP
          </Button>
        </div>
      </form>
    </Modal>
  );
}
